import React from "react";
import { connect } from "react-redux";

/*
  STEP 2: We will be importing SidebarContainer. We will be customising mapStateToProps to map more states from Redux to props,
  so we can begin using topbar.state or projectId, both of which were not available in the mapStateToProps function in the
  core SidebarContainer.js.

  The reason we use the syntax `import { SidebarContainer } from "components/Sidebar/SidebarContainer";` instead of
  `import SidebarContainer from "components/Sidebar/SidebarContainer";` is because the core SidebarContainer is already
  connected to mapStateToProps. When importing components that have been connected to Redux, we need to use the object
  destructuring syntax. Again, we won't be import mapStateToProps here, because we are customising it to include more
  states from Redux.
*/
import { SidebarContainer } from "components/Sidebar/SidebarContainer";
import Sidebar from "components/Sidebar/Sidebar";

/*
  STEP 3: Now we customise mapStateToProps to include more states. We are still keeping `sidebar`` and `language` from
  the mapStateToProps function in the core SidebarContainer.js, so leave them there. We are also adding projectId to be
  retrieved as this.props.pageState and this.props.projectId respectively, so this is how we will include it.
*/
export const mapStateToProps = (state, ownProps) => {
  return {
    sidebar: state.sidebar,
    language: state.language,
    /* Custom: inclusion of new props */
    projectId: state.projectId
  };
};

/*
  STEP 1: From https://docs.google.com/presentation/d/1-lsVhUgHiltw3ztRra8xXADntQa1LJIPwD3Ym8nFr8M/edit#slide=id.gc44c3e3404_0_31,
  we know that we will be removing the sidebar, floating buttons and profile drawer from the Project interface of 3377. The
  information, instead, will be placed in the top-half of the page.

  We will be removing the sidebar from the page. To do so, we need the SidebarContainer.js or Sidebar.js to understand that it will
  not have a sidebar when (1) it is in the Project page, (2) when the Project ID is 3377.

  We wouldn't want to do this using `get sidebarState()` in Pages.js, because Pages.js does not really rely on much information
  on Redux yet. If you read the `render()` logic in core SidebarContainer.js, you will realize that we have already created a
  IS_PROJECT_PAGE variable, which checks if sidebar.state is "project". We can re-use that ... only assuming we do not intend to
  have the sidebar from the Topic Category page too! This is because if you check `get sidebarState()` in Page.js, you will realize
  "project" is applied to _PROJECT_HOME and _TOPIC_CATEGORY.

  Eventually, if you want the sidebar to be available for Topic Category pages, but not Project pages in 3377, you have to be more
  specific than using the state from sidebar. The other way to know "is in Project page" is using topbar.state from Redux. If you
  refer to the `get topbarState()` function in Pages.js, you will realize that _PROJECT_HOME is either "home-single" or "projecthome".
  You can tap on these values to be more specific.

  We have thus fulfilled the 1st condition. To check if the Project ID is indeed 3377, we can retrieve the projectId value from Redux
  too.

  Then, we can handle this using if-else checks for Redux states in CustomSidebarContainer. This logic is handled in the render()
  method of the core SidebarContainer.js, so we would like to do the same in CustomSidebarContainer too.
*/
export class CustomSidebarContainer extends SidebarContainer {
  render() {
    const sidebar = this.props.sidebar;
    const SHOW_TOPIC_SIDEBAR =
      sidebar.state &&
      sidebar.state === "topic" &&
      typeof sidebar.topic === "object" &&
      Object.keys(sidebar.topic).length > 0 &&
      sidebar.project &&
      typeof sidebar.project === "object" &&
      Object.keys(sidebar.project).length > 0;

    const SHOW_PROJECT_SIDEBAR =
      sidebar.state &&
      (sidebar.state === "project" || sidebar.state === "project-secondary") &&
      sidebar.project &&
      typeof sidebar.project === "object" &&
      Object.keys(sidebar.project).length > 0;

    const IS_PROJECT_PAGE = sidebar.state === "project";

    /*
      STEP 5: The original logic here is `if (SHOW_TOPIC_SIDEBAR || SHOW_PROJECT_SIDEBAR)`, let's modify this to exclude (projectId 3377
      and "is project page"). Combining the mentioned logic, we should get `if ( (SHOW_TOPIC_SIDEBAR || SHOW_PROJECT_SIDEBAR) &&
      !(IS_PROJECT_PAGE && this.props.projectId === 3377) )`
    */
    if (
      (SHOW_TOPIC_SIDEBAR || SHOW_PROJECT_SIDEBAR) &&
      !(IS_PROJECT_PAGE && this.props.projectId === 3377)
    ) {
      return (
        <Sidebar
          state={sidebar.state}
          sessionKey={this.props.sessionKey ? this.props.sessionKey : null}
          projectId={sidebar.project.id}
          projectName={sidebar.project.title}
          projectDescription={sidebar.project.description}
          projectImage={sidebar.project.bannerImage}
          project={sidebar.project}
          showTeams={sidebar.project.teamEnabled}
          topicId={sidebar.topic ? sidebar.topic.id : null}
          topicName={sidebar.topic ? sidebar.topic.title : null}
          topicDescription={sidebar.topic ? sidebar.topic.description : null}
          topicImage={sidebar.topic ? sidebar.topic.bannerImage : null}
          totalChallenges={sidebar.topic ? sidebar.topic.challengeNo : null}
          completedChallenges={
            sidebar.topic ? sidebar.topic.userChallengeCompletedNo : null
          }
          isProjectPage={IS_PROJECT_PAGE}
          showShareGameDialog={this.state.showShareGameDialog}
          handleOpenShareGameDialog={this.handleOpenShareGameDialog}
          handleCloseShareGameDialog={this.handleCloseShareGameDialog}
          allowReset={sidebar.topic ? sidebar.topic.allowReset : false}
          showTopicResetActivityDialog={this.state.showTopicResetActivityDialog}
          handleOpenResetTopicActivityDialog={
            this.handleOpenResetTopicActivityDialog
          }
          handleCloseResetTopicActivityDialog={
            this.handleCloseResetTopicActivityDialog
          }
          language={this.props.language}
        />
      );
    } else {
      return null;
    }
  }
}

export default connect(mapStateToProps)(CustomSidebarContainer);
